import { apiClient } from './client';
import {
  EditableStudentField,
  PortalChangeRequest,
  PortalDashboard,
  PortalDocument,
  PortalDocumentRequirements,
  PortalFinalDocuments,
  PortalNotification,
} from '../types/portal';

export const getPortalDashboard = async (): Promise<PortalDashboard> => {
  const { data } = await apiClient.get('/portal/me');
  return data.data;
};

export const listPortalChangeRequests = async (): Promise<PortalChangeRequest[]> => {
  const { data } = await apiClient.get('/portal/change-requests');
  return data.data;
};

export const submitPortalChangeRequest = async (
  fieldName: EditableStudentField,
  newValue: string,
): Promise<PortalChangeRequest> => {
  const { data } = await apiClient.post('/portal/change-requests', { fieldName, newValue });
  return data.data;
};

export const listPortalDocuments = async (includeHistory = false): Promise<PortalDocument[]> => {
  const { data } = await apiClient.get('/portal/documents', {
    params: includeHistory ? { includeHistory: 'true' } : undefined,
  });
  return data.data;
};

/** The student's own document file, fetched with their session (never a
 * public URL). */
export const viewPortalDocument = async (documentId: number): Promise<Blob> => {
  const { data } = await apiClient.get(`/portal/documents/${documentId}/view`, { responseType: 'blob' });
  return data;
};

export const downloadPortalDocument = async (documentId: number): Promise<Blob> => {
  const { data } = await apiClient.get(`/portal/documents/${documentId}/download`, { responseType: 'blob' });
  return data;
};

export const getPortalDocumentRequirements = async (): Promise<PortalDocumentRequirements> => {
  const { data } = await apiClient.get('/portal/document-requirements');
  return data.data;
};

export const getPortalFinalDocuments = async (): Promise<PortalFinalDocuments> => {
  const { data } = await apiClient.get('/portal/final-documents');
  return data.data;
};

export const listPortalNotifications = async (): Promise<PortalNotification[]> => {
  const { data } = await apiClient.get('/portal/notifications');
  return data.data;
};

export const markPortalNotificationsRead = async (): Promise<void> => {
  await apiClient.post('/portal/notifications/read');
};

export interface DocumentUploadOptions {
  expiryDate?: string;
  /** An Other document's name (Phase 103). */
  customName?: string;
  /** True to add a new Other entry; false/omitted replaces the one named. */
  newEntry?: boolean;
  /** Upload progress, 0–100. */
  onProgress?: (percent: number) => void;
}

/** The multipart form both upload endpoints take. */
export const documentUploadForm = (file: File, documentTypeId: number, options: DocumentUploadOptions = {}) => {
  const form = new FormData();
  form.append('file', file);
  form.append('documentTypeId', String(documentTypeId));
  if (options.expiryDate) form.append('expiryDate', options.expiryDate);
  if (options.customName) form.append('customName', options.customName);
  if (options.newEntry !== undefined) form.append('newEntry', String(options.newEntry));
  return form;
};

export const uploadProgressHandler = (onProgress?: (percent: number) => void) =>
  onProgress
    ? (event: { loaded: number; total?: number }) => {
        if (event.total) onProgress(Math.round((event.loaded / event.total) * 100));
      }
    : undefined;

export const uploadPortalDocument = async (
  file: File,
  documentTypeId: number,
  options: DocumentUploadOptions = {},
): Promise<PortalDocument> => {
  const { data } = await apiClient.post('/portal/documents', documentUploadForm(file, documentTypeId, options), {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: uploadProgressHandler(options.onProgress),
  });
  return data.data;
};
